import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import React, { useRef, useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';

import { alpha, styled } from '@mui/material/styles';
import { Box, Card, Fade, Stack, Drawer, useTheme, Container } from '@mui/material';
import {
  MuiEvent,
  GridRowId,
  GridColDef,
  DataGridPro,
  gridClasses,
  GridDensity,
  GridRowModel,
  ValueOptions,
  GridSortModel,
  useGridApiRef,
  GridFilterModel,
  GridValidRowModel,
  GridCallbackDetails,
  GridPaginationModel,
  getGridDateOperators,
  GridRowSelectionModel,
  getGridStringOperators,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridColumnOrderChangeParams,
} from '@mui/x-data-grid-pro';

import useNotifications from 'src/hooks/useNotifications';

import { GRID_LT_LOCALE_TEXT } from 'src/locales/data-grid-locales/lt';
import { GRID_LV_LOCALE_TEXT } from 'src/locales/data-grid-locales/lv';

import DocumentGridToolbar from 'src/sections/documents/DocumentGridToolbar';

import Page from '../../components/Page';
import { useSelector } from '../../redux/store';
import useLocales from '../../hooks/useLocales';
import tagService from '../../services/tagService';
import { PATH_DOCUMENTS } from '../../routes/paths';
import useResponsive from '../../hooks/useResponsive';
import useAppSettings from '../../hooks/useAppSettings';
import { TagsFilter } from '../../@types/tags/tagsFilter';
import { getFolder } from '../../services/dlxDriveService';
import eventBus, { EventTypes } from '../../utils/eventBus';
import documentService from '../../services/documentService';
import { setCheckedItems } from '../../redux/slices/document';
import FolderTitle from '../../sections/dlx-drive/FolderTitle';
import { ModuleTypes } from '../../@types/module/enums/moduleTypes';
import { DocumentsRequestProps } from '../../@types/documents/types';
import editSearchParams from '../../utils/documents/editSearchParams';
import { GRID_EN_LOCALE_TEXT } from '../../locales/data-grid-locales/en';
import { GRID_EE_LOCALE_TEXT } from '../../locales/data-grid-locales/ee';
import { GRID_RU_LOCALE_TEXT } from '../../locales/data-grid-locales/ru';
import FolderTree from '../../sections/dlx-drive/folder-tree/FolderTree';
import RenderDateTimeField from '../../utils/folders/renderDateTimeField';
import FolderBreadcrumbs from '../../sections/dlx-drive/FolderBreadcrumbs';
import RenderDateRangeField from '../../utils/folders/renderDateRangeField';
import FolderFolderActions from '../../sections/dlx-drive/FolderFolderActions';
import { DocumentStatuses } from '../../@types/documents/enums/documentStatuses';
import FolderDocumentActions from '../../sections/dlx-drive/FolderDocumentActions';
import { PAGE_SIZE, INITIAL_STATE } from '../../@types/documents/constants/InitialValues';
import DLXDriveBulkActionComponent from '../../sections/dlx-drive/DLXDriveBulkActionComponent';
import {
  COMMENT_COLUMN,
  DEFAULT_COLUMNS,
  EXPIRATION_DATE_COLUMN,
} from '../../@types/documents/constants/Columns';
import {
  addBreadcrumbsState,
  addStopLoadingState,
  addStartLoadingState,
  addSelectedFolderState,
} from '../../redux/slices/dlxDrive';

// ----------------------------------------------------------------

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  border: 'none',
  // even row
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
        cursor: 'pointer',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
        ),
        cursor: 'pointer',
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
          cursor: 'pointer',
        },
      },
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },

  // odd row
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: theme.palette.grey[50],
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
        cursor: 'pointer',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity
        ),
        cursor: 'pointer',
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
          cursor: 'pointer',
        },
      },
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

// ----------------------------------------------------------------

const Folders = () => {
  const { t, currentLang } = useLocales();
  const theme = useTheme();
  const modulesData = useAppSettings();
  const { breadcrumbs, reloadDriveList } = useSelector((state) => state.dlxDrive);
  const [pageTitle, setPageTitle] = useState<string>(t('layout.dlxDrive'));
  const [showFolderTree, setShowFolderTree] = useState<boolean>(() => {
    const showFolderTree = localStorage.getItem('showFolderTree');
    return showFolderTree ? JSON.parse(showFolderTree) : true;
  });
  const { enqueueSuccess, enqueueError } = useNotifications();
  const { folderId } = useParams();

  const navigate = useNavigate();
  const isMobile = useResponsive('only', 'xs');
  const showDrawer = useResponsive('down', 'lg');
  const [folderTreeExpandedItems, setFolderTreeExpandedItems] = useState<string[]>(() => {
    const folderTreeExpandedItems = localStorage.getItem('folderTreeExpandedItems');

    return folderTreeExpandedItems ? JSON.parse(folderTreeExpandedItems) : [];
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();

  const [localeText, setLocaleText] = useState(GRID_EN_LOCALE_TEXT);

  const filterModelParams = searchParams.get('filterModel');
  const sortModelParams = searchParams.get('sortModel');
  const paginationModelParams = searchParams.get('paginationModel');
  const formTemplateIdParams = searchParams.get('formTemplateId');

  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortModel, setSortModel] = useState<GridSortModel>();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => {
      const newColumnVisibilityModel = localStorage.getItem('columnVisibilityModel');

      if (newColumnVisibilityModel) {
        return JSON.parse(newColumnVisibilityModel);
      }

      return {};
    }
  );

  const { checkedItems } = useSelector((state) => state.document);
  const { isLoading } = useSelector((state) => state.dlxDrive);

  const [rows, setRows] = useState<GridRowModel[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);

  const [columns, setColumns] = useState<GridColDef<GridValidRowModel>[]>([]);
  const [defaultColumns, setDefaultColumns] =
    useState<GridColDef<GridValidRowModel>[]>(DEFAULT_COLUMNS);
  const [customColumns, setCustomColumns] = useState<GridColDef<GridValidRowModel>[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const savedColumns = localStorage.getItem('columns');
    if (savedColumns) {
      const parsedColumns = JSON.parse(savedColumns);
      return parsedColumns.map((col: { field: string; width: number }) => col.field);
    }
    return [];
  });

  const [density, setDensity] = useState<GridDensity>(() => {
    const density = localStorage.getItem('density');

    return density ? (density as GridDensity) : 'standard';
  });

  const [tags, setTags] = useState<ValueOptions[]>([]);

  const apiRef = useGridApiRef();

  const isLargeScreen = useResponsive('up', 'md');
  const isMediumScreen = useResponsive('up', 'sm');
  const width = isLargeScreen ? 700 : isMediumScreen ? 500 : 400;

  const pageSizeOptions = [PAGE_SIZE, 20, 50];

  const hiddenFields = ['id', 'extension', 'signerDetails'];

  const getTogglableColumns = (columns: GridColDef[]) =>
    columns.filter((column) => !hiddenFields.includes(column.field)).map((column) => column.field);

  const autosizeOptions = {
    includeHeaders: true,
    includeOutliers: true,
    outliersFactor: 2,
    expand: true,
  };

  const getFolderData = async () => {
    addStartLoadingState();

    try {
      const data = await getFolder(folderId ? Number(folderId) : null);

      const { id, name, actions, prefix, breadcrumbs, color, icon } = data;

      addSelectedFolderState({ id, name, prefix, actions, color, icon });

      addBreadcrumbsState(breadcrumbs);
    } catch (error) {
      if (error instanceof Error) {
        enqueueError(error.message);
      }
    } finally {
      addStopLoadingState();
    }
  };

  const getDocuments = async (request: DocumentsRequestProps) => {
    addStartLoadingState();

    try {
      const response = await documentService.getDocuments(request);

      const { rows, rowCount, columns } = response;

      if (columns.length > 0) {
        const newColumns: GridColDef<GridValidRowModel>[] = [];

        columns.map((column: GridColDef<GridValidRowModel>) => {
          if (column.type === 'date' || column.type === 'dateTime') {
            const newColumnProps = {
              minWidth: 160,
              valueGetter: (value: string) => new Date(value),
              renderCell: (params) => {
                const date = params.row[params.field];

                return (
                  <RenderDateTimeField date={date} format={column.type as 'date' | 'dateTime'} />
                );
              },
              filterOperators: getGridDateOperators().filter(
                (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
              ),
            };

            newColumns.push({ ...column, ...newColumnProps });
          } else if (column.type === 'string') {
            const newColumnProps = {
              minWidth: 160,
              filterOperators: getGridStringOperators().filter(
                (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
              ),
            };

            newColumns.push({ ...column, ...newColumnProps });
          } else if (column.type === 'custom') {
            const newColumnProps = {
              minWidth: 160,
              valueGetter: (value: string) => value,
              renderCell: (params) => {
                const dateRange = params.row[params.field];

                if (dateRange) {
                  return <RenderDateRangeField dateRange={dateRange} />;
                }

                return '';
              },
              sortable: false,
              filterable: false,
            };

            newColumns.push({ ...column, ...newColumnProps });
          } else {
            newColumns.push(column);
          }
        });

        setCustomColumns(newColumns);
      } else {
        setCustomColumns(columns);
      }

      setRows(rows);
      setRowCount(rowCount);
    } catch (e) {
      enqueueError(e);
    } finally {
      addStopLoadingState();
    }
  };

  const debouncedGetDocuments = useRef(
    debounce(
      async (
        filterModelParams: string | null,
        sortModelParams: string | null,
        paginationModelParams: string | null,
        formTemplateIdParams: string | null,
        folderId: string | undefined
      ) => {
        const documentsRequest = editSearchParams(
          filterModelParams,
          sortModelParams,
          paginationModelParams,
          formTemplateIdParams,
          folderId
        );

        await getDocuments(documentsRequest);
      },
      500
    )
  ).current;

  const getTags = async () => {
    const tagsFilter = new TagsFilter();
    tagsFilter.limit = 999999;
    tagsFilter.offset = 0;
    tagsFilter.searchQuery = null;
    tagsFilter.sortingColumn = null;
    tagsFilter.sortDescending = null;

    const data = await tagService.getPagedTags(tagsFilter);
    const newTags = data.items.map((tag) => ({ label: tag.name, value: tag.id }));

    setTags(newTags);
  };

  // filterModel
  const handleFilterModelChange = (newModel: GridFilterModel) => {
    if (
      newModel.items.length > 0 ||
      (newModel.quickFilterValues && newModel.quickFilterValues.length > 0)
    ) {
      searchParams.set('filterModel', JSON.stringify(newModel));
      setSearchParams(searchParams);
    } else {
      searchParams.delete('filterModel');
      setSearchParams(searchParams);
    }
  };

  // sortModel
  const handleSortModelChange = (newModel: GridSortModel) => {
    if (newModel.length > 0) {
      searchParams.set('sortModel', JSON.stringify(newModel));
      setSearchParams(searchParams);
    } else {
      searchParams.delete('sortModel');
      setSearchParams(searchParams);
    }
  };

  // paginationModel
  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    if (newModel) {
      searchParams.set('paginationModel', JSON.stringify(newModel));
      setSearchParams(searchParams);
      localStorage.setItem('pageSize', newModel.pageSize.toString());
    } else {
      searchParams.delete('paginationModel');
      setSearchParams(searchParams);
    }
  };

  const handleRowSelectionModelChange = (
    newRowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    const allRows = details.api.getRowModels();

    let selectedIds: GridRowId[];
    if (newRowSelectionModel.type === 'include') {
      selectedIds = Array.from(newRowSelectionModel.ids);
    } else {
      const allIds = Array.from(allRows.keys());
      selectedIds = allIds.filter((id) => !newRowSelectionModel.ids.has(id));
    }

    // Get current page selected rows
    const currentPageSelectedRows = selectedIds
      .map((id) => allRows.get(id))
      .filter((r): r is GridRowModel => r != null);

    // Get current page row IDs for comparison
    const currentPageRowIds = Array.from(allRows.keys());

    // Keep existing selections that are NOT from current page
    const preservedSelections = checkedItems.filter((item) => !currentPageRowIds.includes(item.id));

    // Combine preserved selections with current page selections
    const updatedCheckedItems = [...preservedSelections, ...currentPageSelectedRows];

    setCheckedItems(updatedCheckedItems);
  };

  // filterModel
  useEffect(() => {
    setFilterModel(filterModelParams ? JSON.parse(filterModelParams) : { items: [] });
  }, [filterModelParams]);

  // sortModel
  useEffect(() => {
    setSortModel(sortModelParams ? JSON.parse(sortModelParams) : []);
  }, [sortModelParams]);

  // paginationModel
  useEffect(() => {
    const savedPageSize = localStorage.getItem('pageSize');

    if (paginationModelParams) {
      setPaginationModel(JSON.parse(paginationModelParams));
    }

    if (!paginationModelParams && savedPageSize) {
      setPaginationModel({ pageSize: Number(savedPageSize), page: 0 });
    }
  }, [paginationModelParams]);

  // rowSelectionModel
  useEffect(() => {
    const ids: string[] = checkedItems.map((item) => item.id);
    const newRowSelectionModel: GridRowSelectionModel = {
      type: 'include',
      ids: new Set(ids),
    };

    checkedItems.map((item) => newRowSelectionModel.ids.add(item.id));

    setRowSelectionModel(newRowSelectionModel);
  }, [checkedItems]);

  useEffect(() => {
    debouncedGetDocuments(
      filterModelParams,
      sortModelParams,
      paginationModelParams,
      formTemplateIdParams,
      folderId
    );
  }, [
    filterModelParams,
    sortModelParams,
    paginationModelParams,
    formTemplateIdParams,
    folderId,
    reloadDriveList,
  ]);

  // location
  useEffect(() => {
    if (location && location.state && location.state.status && location.state.status.filterModel) {
      searchParams.set('filterModel', location.state.status.filterModel);

      setSearchParams(searchParams);
    }
  }, [location]);

  // cancel debounce
  useEffect(
    () => () => {
      debouncedGetDocuments.cancel();
    },
    [debouncedGetDocuments]
  );

  useEffect(() => {
    if (breadcrumbs.length > 0) {
      const newTitle = `${breadcrumbs[breadcrumbs.length - 1].name} - ${t('layout.dlxDrive')}`;

      if (pageTitle !== newTitle) {
        setPageTitle(newTitle);
      }
    }
  }, [breadcrumbs]);

  useEffect(() => {
    localStorage.setItem('showFolderTree', JSON.stringify(showFolderTree));
  }, [showFolderTree, folderTreeExpandedItems]);

  useEffect(() => {
    localStorage.setItem('folderTreeExpandedItems', JSON.stringify(folderTreeExpandedItems));
  }, [folderTreeExpandedItems]);

  useEffect(() => {
    getFolderData().then();
  }, [folderId]);

  useEffect(() => {
    switch (currentLang.value.toString()) {
      case 'lt':
        setLocaleText(GRID_LT_LOCALE_TEXT);
        break;

      case 'lv':
        setLocaleText(GRID_LV_LOCALE_TEXT);
        break;

      case 'ee':
        setLocaleText(GRID_EE_LOCALE_TEXT);
        break;

      case 'ru':
        setLocaleText(GRID_RU_LOCALE_TEXT);
        break;

      default:
        setLocaleText(GRID_EN_LOCALE_TEXT);
    }
  }, [currentLang]);

  useEffect(() => {
    const newDefaultColumns = defaultColumns.map((column) => {
      if (column.field === 'tags') {
        return {
          ...column,
          valueOptions: tags,
        };
      }

      return column;
    });

    setDefaultColumns(newDefaultColumns);
  }, [tags]);

  useEffect(() => {
    const savedColumns = localStorage.getItem('columns');

    const newColumns = [...defaultColumns, ...customColumns];

    const hasCommentModule = modulesData.modules.includes(ModuleTypes.StaticFieldModule);
    const hasDocumentExpirationModule = modulesData.modules.includes(
      ModuleTypes.DocumentDeadlineModule
    );

    if (hasCommentModule) {
      newColumns.push(...COMMENT_COLUMN);
    }

    if (hasDocumentExpirationModule) {
      newColumns.push(...EXPIRATION_DATE_COLUMN);
    }

    if (savedColumns) {
      const parsedColumns = JSON.parse(savedColumns);

      parsedColumns.map((column: GridColDef<GridValidRowModel>) => {
        const foundColumn = newColumns.find((newColumn) => newColumn.field === column.field);

        if (foundColumn) {
          foundColumn.width = column.width;
        }
      });

      // Apply saved column order
      if (columnOrder.length > 0) {
        const orderedColumns: GridColDef<GridValidRowModel>[] = [];
        const remainingColumns: GridColDef<GridValidRowModel>[] = [];

        // First, add columns in saved order
        columnOrder.forEach((field) => {
          const column = newColumns.find((col) => col.field === field);
          if (column) {
            orderedColumns.push(column);
          }
        });

        // Then add any new columns that weren't in saved order
        newColumns.forEach((column) => {
          if (!columnOrder.includes(column.field)) {
            remainingColumns.push(column);
          }
        });

        setColumns([...orderedColumns, ...remainingColumns]);
        return;
      }
    }

    setColumns(newColumns);
  }, [defaultColumns, customColumns, columnOrder]);

  useEffect(() => {
    addStartLoadingState();

    getTags().then();

    const documentSignedSubscription = eventBus.subscribe(
      EventTypes.DocumentSigned,
      (msg: string) => {
        enqueueSuccess(msg);
      }
    );

    const forbidSignSubscription = eventBus.subscribe(EventTypes.ForbidSign, (msg: string) => {
      enqueueError(msg);
    });

    return () => {
      documentSignedSubscription.unsubscribe();
      forbidSignSubscription.unsubscribe();
    };
  }, []);

  return (
    <Page title={pageTitle}>
      <Container maxWidth={showFolderTree ? false : 'xl'}>
        <Stack direction="row" spacing={4}>
          {showFolderTree && !showDrawer && (
            <Card sx={{ width: { xl: '25%', lg: '30%' } }}>
              <FolderTree
                showDrawer={showDrawer || false}
                setShowFolderTree={setShowFolderTree}
                folderTreeExpandedItems={folderTreeExpandedItems}
                setFolderTreeExpandedItems={setFolderTreeExpandedItems}
              />
            </Card>
          )}

          <Box
            sx={{
              width: showFolderTree && !showDrawer ? { xl: '75%', lg: '70%' } : '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <FolderBreadcrumbs
              showFolderTree={showFolderTree}
              setIsDrawerOpen={setShowFolderTree}
            />

            <Box
              sx={[
                {
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                },
                isMobile
                  ? {
                      flexDirection: 'column',
                    }
                  : {
                      flexDirection: 'row',
                    },
                isMobile
                  ? {
                      alignItems: 'left',
                    }
                  : {
                      alignItems: 'center',
                    },
              ]}
            >
              <Box
                id="dfODF45"
                sx={{
                  flexGrow: 1,
                  overflow: 'hidden'
                }}>
                <FolderTitle />
              </Box>

              <FolderFolderActions isRootFolder={breadcrumbs.length === 1} />
            </Box>

            <FolderDocumentActions folderId={Number(folderId)} />

            <Box
              // ref={itemWrapperWidth}
              sx={[
                {
                  position: 'relative',
                  width: '100%',

                  '& .documents-grid--header': {
                    backgroundColor: theme.palette.background.default,
                  },
                },
                breadcrumbs.length > 1
                  ? {
                      height: 'calc(100vh - 342px)',
                    }
                  : {
                      height: 'calc(100vh - 242px)',
                    },
              ]}
            >
              <StripedDataGrid
                apiRef={apiRef}
                initialState={INITIAL_STATE}
                columns={columns}
                rows={rows}
                rowCount={rowCount}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                filterMode="server"
                filterModel={filterModel}
                sortingMode="server"
                sortModel={sortModel}
                checkboxSelection
                disableRowSelectionOnClick
                onRowClick={(params) => {
                  const { id, status, folderId, fbFilledForm } = params.row;

                  if (
                    // status === DocumentStatuses.InProgress ||
                    status === DocumentStatuses.WaitingForMeToFillForm ||
                    status === DocumentStatuses.WaitingForOthersToFillForm ||
                    status === DocumentStatuses.FormFilled
                  ) {
                    let route = '';

                    route = PATH_DOCUMENTS.editDocument(folderId, fbFilledForm.id, id, 0);

                    navigate(route);
                  } else {
                    navigate(`/standard/documents/${id}`);
                  }
                }}
                pageSizeOptions={pageSizeOptions}
                slots={{
                  toolbar: DocumentGridToolbar,
                }}
                showToolbar
                slotProps={{
                  toolbar: { showQuickFilter: true },
                  loadingOverlay: {
                    variant: 'skeleton',
                    noRowsVariant: 'skeleton',
                  },
                  columnsManagement: {
                    getTogglableColumns,
                  },
                }}
                autosizeOptions={autosizeOptions}
                onPaginationModelChange={(newPaginationModel) => {
                  handlePaginationModelChange(newPaginationModel);
                }}
                onSortModelChange={(newSortModel) => {
                  handleSortModelChange(newSortModel);
                }}
                onFilterModelChange={(newFilterModel) => {
                  handleFilterModelChange(newFilterModel);
                }}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                rowSelectionModel={rowSelectionModel}
                loading={isLoading}
                localeText={localeText}
                dataSourceCache={null}
                density={density}
                onDensityChange={(density: GridDensity) => {
                  setDensity(density);
                  localStorage.setItem('density', density);
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel: GridColumnVisibilityModel) => {
                  setColumnVisibilityModel(newModel);
                  localStorage.setItem('columnVisibilityModel', JSON.stringify(newModel));
                }}
                onColumnOrderChange={(
                  _params: GridColumnOrderChangeParams,
                  _event: MuiEvent<{}>,
                  details: GridCallbackDetails
                ) => {
                  const allColumns = details.api.getAllColumns().map((column) => ({
                    field: column.field,
                    width: column.width,
                  }));
                  localStorage.setItem('columns', JSON.stringify(allColumns));
                  setColumnOrder(allColumns.map((col) => col.field));
                }}
                onColumnResize={(
                  _params: GridColumnResizeParams,
                  _event: MuiEvent,
                  details: GridCallbackDetails
                ) => {
                  const allColumns = details.api.getAllColumns().map((column) => ({
                    field: column.field,
                    width: column.width,
                  }));
                  localStorage.setItem('columns', JSON.stringify(allColumns));
                }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
              />

              <Fade in={checkedItems.length > 0} timeout={800}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={[
                      {
                        position: 'fixed',
                        bottom: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        px: 1,
                        boxSizing: 'border-box',
                      },
                      width <= 800
                        ? {
                            width: '100%',
                            maxWidth: `${width}px`,
                          }
                        : {
                            width: '100%',
                            maxWidth: '800px',
                          },
                    ]}
                  >
                    <DLXDriveBulkActionComponent />
                  </Box>
                </Box>
              </Fade>
            </Box>
          </Box>
        </Stack>
      </Container>
      <Drawer open={showDrawer && showFolderTree} onClose={() => setShowFolderTree(false)}>
        <FolderTree
          showDrawer={showDrawer || false}
          setShowFolderTree={setShowFolderTree}
          folderTreeExpandedItems={folderTreeExpandedItems}
          setFolderTreeExpandedItems={setFolderTreeExpandedItems}
        />
      </Drawer>
    </Page>
  );
};

export default Folders;
